import React from 'react';
import { 
  FileText, Combine, Scissors, Minimize2, RotateCw, FileOutput, Trash2, 
  ListOrdered, Stamp, Lock, Unlock, FileSearch, FileEdit, Hash, Calculator, 
  Eye, Image as ImageIcon, Crop, Sparkles, Palette, Moon, Contrast, ScanLine, 
  GitCompare, PenTool, MessageSquare, Type, Ruler, Scale, Code2, Binary, Zap, 
  ShieldCheck, Share2, KeyRound, QrCode, Clock, DollarSign, FolderArchive, 
  Layers, Link, Fingerprint, Monitor, Database, Terminal, FileCode, Sliders, 
  RefreshCw, Cpu, Activity, Gauge, ZapOff, Bookmark, Star, Wrench, Grid, List
} from 'lucide-react';
import { ExtendedToolItem } from './allToolsData';

export interface ToolVisualTheme {
  icon: React.ElementType;
  bgClass: string;
  textClass: string;
  borderClass: string;
}

export const COLOR_PALETTES = [
  { id: 'red', bgClass: 'bg-gradient-to-tr from-red-500 to-rose-600 shadow-md shadow-red-500/30', textClass: 'text-white', borderClass: 'border-red-400/30' },
  { id: 'orange', bgClass: 'bg-gradient-to-tr from-orange-500 to-amber-600 shadow-md shadow-orange-500/30', textClass: 'text-white', borderClass: 'border-orange-400/30' },
  { id: 'amber', bgClass: 'bg-gradient-to-tr from-amber-500 to-yellow-600 shadow-md shadow-amber-500/30', textClass: 'text-white', borderClass: 'border-amber-400/30' },
  { id: 'yellow', bgClass: 'bg-gradient-to-tr from-yellow-500 to-amber-500 shadow-md shadow-yellow-500/30', textClass: 'text-white', borderClass: 'border-yellow-400/30' },
  { id: 'lime', bgClass: 'bg-gradient-to-tr from-lime-500 to-emerald-600 shadow-md shadow-lime-500/30', textClass: 'text-white', borderClass: 'border-lime-400/30' },
  { id: 'emerald', bgClass: 'bg-gradient-to-tr from-emerald-500 to-teal-600 shadow-md shadow-emerald-500/30', textClass: 'text-white', borderClass: 'border-emerald-400/30' },
  { id: 'teal', bgClass: 'bg-gradient-to-tr from-teal-500 to-cyan-600 shadow-md shadow-teal-500/30', textClass: 'text-white', borderClass: 'border-teal-400/30' },
  { id: 'cyan', bgClass: 'bg-gradient-to-tr from-cyan-500 to-blue-600 shadow-md shadow-cyan-500/30', textClass: 'text-white', borderClass: 'border-cyan-400/30' },
  { id: 'sky', bgClass: 'bg-gradient-to-tr from-sky-500 to-blue-600 shadow-md shadow-sky-500/30', textClass: 'text-white', borderClass: 'border-sky-400/30' },
  { id: 'blue', bgClass: 'bg-gradient-to-tr from-blue-500 to-indigo-600 shadow-md shadow-blue-500/30', textClass: 'text-white', borderClass: 'border-blue-400/30' },
  { id: 'indigo', bgClass: 'bg-gradient-to-tr from-indigo-500 to-purple-600 shadow-md shadow-indigo-500/30', textClass: 'text-white', borderClass: 'border-indigo-400/30' },
  { id: 'violet', bgClass: 'bg-gradient-to-tr from-violet-500 to-purple-600 shadow-md shadow-violet-500/30', textClass: 'text-white', borderClass: 'border-violet-400/30' },
  { id: 'purple', bgClass: 'bg-gradient-to-tr from-purple-500 to-pink-600 shadow-md shadow-purple-500/30', textClass: 'text-white', borderClass: 'border-purple-400/30' },
  { id: 'fuchsia', bgClass: 'bg-gradient-to-tr from-fuchsia-500 to-pink-600 shadow-md shadow-fuchsia-500/30', textClass: 'text-white', borderClass: 'border-fuchsia-400/30' },
  { id: 'pink', bgClass: 'bg-gradient-to-tr from-pink-500 to-rose-600 shadow-md shadow-pink-500/30', textClass: 'text-white', borderClass: 'border-pink-400/30' },
  { id: 'rose', bgClass: 'bg-gradient-to-tr from-rose-500 to-red-600 shadow-md shadow-rose-500/30', textClass: 'text-white', borderClass: 'border-rose-400/30' },
];

export function getCategoryGradient(categoryId?: string): { bgClass: string; textClass: string } {
  switch (categoryId) {
    case 'pdf-tools':
      return { bgClass: 'bg-gradient-to-tr from-red-500 to-rose-600 shadow-md shadow-red-500/30', textClass: 'text-white' };
    case 'image-tools':
      return { bgClass: 'bg-gradient-to-tr from-emerald-400 to-teal-500 shadow-md shadow-emerald-500/30', textClass: 'text-white' };
    case 'text-tools':
      return { bgClass: 'bg-gradient-to-tr from-purple-500 to-indigo-600 shadow-md shadow-purple-500/30', textClass: 'text-white' };
    case 'developer-tools':
      return { bgClass: 'bg-gradient-to-tr from-blue-500 to-indigo-600 shadow-md shadow-blue-500/30', textClass: 'text-white' };
    case 'color-tools':
      return { bgClass: 'bg-gradient-to-tr from-amber-400 to-orange-500 shadow-md shadow-amber-500/30', textClass: 'text-white' };
    case 'security-tools':
      return { bgClass: 'bg-gradient-to-tr from-teal-400 to-emerald-600 shadow-md shadow-teal-500/30', textClass: 'text-white' };
    case 'social-tools':
      return { bgClass: 'bg-gradient-to-tr from-pink-500 to-rose-500 shadow-md shadow-pink-500/30', textClass: 'text-white' };
    case 'calculators':
      return { bgClass: 'bg-gradient-to-tr from-amber-500 to-yellow-500 shadow-md shadow-amber-500/30', textClass: 'text-white' };
    case 'file-tools':
      return { bgClass: 'bg-gradient-to-tr from-blue-500 to-cyan-500 shadow-md shadow-blue-500/30', textClass: 'text-white' };
    case 'data-tools':
      return { bgClass: 'bg-gradient-to-tr from-purple-500 to-fuchsia-600 shadow-md shadow-purple-500/30', textClass: 'text-white' };
    case 'productivity-tools':
      return { bgClass: 'bg-gradient-to-tr from-lime-500 to-emerald-600 shadow-md shadow-lime-500/30', textClass: 'text-white' };
    case 'unit-conversion':
      return { bgClass: 'bg-gradient-to-tr from-cyan-400 to-teal-500 shadow-md shadow-cyan-500/30', textClass: 'text-white' };
    case 'time-date-tools':
      return { bgClass: 'bg-gradient-to-tr from-rose-500 to-pink-500 shadow-md shadow-rose-500/30', textClass: 'text-white' };
    case 'financial-tools':
    case 'finance-tools':
      return { bgClass: 'bg-gradient-to-tr from-emerald-500 to-green-600 shadow-md shadow-emerald-500/30', textClass: 'text-white' };
    default:
      return { bgClass: 'bg-gradient-to-tr from-violet-500 to-purple-600 shadow-md shadow-violet-500/30', textClass: 'text-white' };
  }
}

const FALLBACK_ICONS: React.ElementType[] = [
  Wrench, Layers, Zap, Cpu, Activity, Gauge, Sliders, RefreshCw, Sparkles, Terminal, FileCode
];

// Helper to string hash
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

// Get Icon for specific tool keyword
function getIconForTool(toolId: string, toolName: string): React.ElementType {
  const q = (toolId + ' ' + toolName).toLowerCase();

  if (q.includes('merge') || q.includes('combine') || q.includes('join')) return Combine;
  if (q.includes('split') || q.includes('cut') || q.includes('slice')) return Scissors;
  if (q.includes('compress') || q.includes('shrink') || q.includes('minify') || q.includes('minifier')) return Minimize2;
  if (q.includes('rotate') || q.includes('flip') || q.includes('orientation')) return RotateCw;
  if (q.includes('extract')) return FileOutput;
  if (q.includes('remove') || q.includes('delete') || q.includes('strip') || q.includes('blank')) return Trash2;
  if (q.includes('reorder') || q.includes('sort') || q.includes('shuffle')) return ListOrdered;
  if (q.includes('watermark') || q.includes('stamp')) return Stamp;
  if (q.includes('unlock') || q.includes('decrypt')) return Unlock;
  if (q.includes('protect') || q.includes('lock') || q.includes('encrypt') || q.includes('security')) return Lock;
  if (q.includes('meta') || q.includes('info') || q.includes('inspect') || q.includes('exif')) return FileSearch;
  if (q.includes('edit') || q.includes('editor') || q.includes('bookmark')) return FileEdit;
  if (q.includes('count') || q.includes('counter') || q.includes('calc') || q.includes('math')) return Calculator;
  if (q.includes('view') || q.includes('viewer') || q.includes('read') || q.includes('preview')) return Eye;
  if (q.includes('thumb') || q.includes('ico') || q.includes('favicon') || q.includes('avatar') || q.includes('meme')) return Sparkles;
  if (q.includes('crop') || q.includes('circle')) return Crop;
  if (q.includes('pdf')) return FileText;
  if (q.includes('image') || q.includes('png') || q.includes('jpg') || q.includes('webp') || q.includes('gif') || q.includes('heic') || q.includes('svg')) return ImageIcon;
  if (q.includes('word') || q.includes('text') || q.includes('lorem') || q.includes('slug') || q.includes('case') || q.includes('paragraph') || q.includes('sentence')) return Type;
  if (q.includes('color') || q.includes('palette') || q.includes('gradient') || q.includes('hue') || q.includes('sat') || q.includes('sepia')) return Palette;
  if (q.includes('invert') || q.includes('dark') || q.includes('negative')) return Moon;
  if (q.includes('blur') || q.includes('sharpen') || q.includes('contrast') || q.includes('filter')) return Contrast;
  if (q.includes('ocr') || q.includes('scan')) return ScanLine;
  if (q.includes('compare') || q.includes('diff')) return GitCompare;
  if (q.includes('sign') || q.includes('signature')) return PenTool;
  if (q.includes('annotation') || q.includes('comment')) return MessageSquare;
  if (q.includes('margin') || q.includes('dimension') || q.includes('ratio') || q.includes('dpi') || q.includes('viewport') || q.includes('size')) return Ruler;
  if (q.includes('json') || q.includes('yaml') || q.includes('xml') || q.includes('sql') || q.includes('html') || q.includes('css') || q.includes('js') || q.includes('dev') || q.includes('regex')) return Code2;
  if (q.includes('binary')) return Binary;
  if (q.includes('hash') || q.includes('md5') || q.includes('sha') || q.includes('bcrypt') || q.includes('hmac')) return Zap;
  if (q.includes('qr')) return QrCode;
  if (q.includes('jwt') || q.includes('token') || q.includes('key')) return KeyRound;
  if (q.includes('uuid') || q.includes('nano') || q.includes('id')) return Fingerprint;
  if (q.includes('cron') || q.includes('time') || q.includes('clock') || q.includes('date') || q.includes('epoch') || q.includes('timestamp') || q.includes('zone')) return Clock;
  if (q.includes('device') || q.includes('browser') || q.includes('screen') || q.includes('user-agent') || q.includes('ua')) return Monitor;
  if (q.includes('storage') || q.includes('cookie')) return Database;
  if (q.includes('social') || q.includes('share') || q.includes('youtube') || q.includes('instagram') || q.includes('tweet')) return Share2;
  if (q.includes('tax') || q.includes('loan') || q.includes('tip') || q.includes('money') || q.includes('dollar') || q.includes('finance')) return DollarSign;
  if (q.includes('zip') || q.includes('archive') || q.includes('file')) return FolderArchive;
  if (q.includes('url') || q.includes('link')) return Link;

  // Fallback to deterministic hash icon
  const h = hashString(toolId);
  return FALLBACK_ICONS[h % FALLBACK_ICONS.length];
}

/**
 * Returns distinct visual theme (Icon and Colors) for a specific tool item
 */
export function getToolVisualTheme(tool: Partial<ExtendedToolItem> & { id: string; name?: string; categoryId?: string }): ToolVisualTheme {
  const toolId = tool.id || 'tool';
  const toolName = tool.name || toolId;

  // Compute a deterministic color palette index based on the tool's ID so every tool has its own unique vibrant color
  const h = hashString(toolId);
  const colorIndex = h % COLOR_PALETTES.length;
  const palette = COLOR_PALETTES[colorIndex];

  // Pick unique icon
  const icon = getIconForTool(toolId, toolName);

  return {
    icon,
    bgClass: palette.bgClass,
    textClass: palette.textClass,
    borderClass: palette.borderClass,
  };
}
